import React from 'react';
import { ResponsiveContainer, Treemap } from 'recharts';
import PrintLayout from '../PrintLayout';
import AverageTalkTimeChart from '../../charts/AverageTalkTimeChart';
import CommentsVsQuestionsChart from '../../charts/CommentsVsQuestionsChart';
import FrequencyChart from '../../charts/FrequencyChart';

const COLORS = ['#2a3d5b', '#5f7942', '#cba43a', '#922b3f', '#7209b7', '#4cc9f0'];

function Card({ title, caption, children }) {
  return (
    <div style={{ background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#2a3d5b', marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 8.5, color: '#6b757e', marginBottom: 6 }}>{caption}</div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// Above this many participants, a 2x2 grid squeezes each chart (especially the
// Speaking Frequency circle and Occupying Meeting Space treemap) too small to read —
// switch to two pages of 2 charts each, side by side at full card height.
const SPLIT_THRESHOLD = 8;

function TreemapCard({ treemapData }) {
  return (
    <Card title="Occupying Meeting Space" caption="Each block is one person's share of total talk time">
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={treemapData}
          dataKey="size"
          aspectRatio={4 / 3}
          isAnimationActive={false}
          fill="transparent"
          content={({ x, y, width, height, name, color }) => {
            const showLabel = width > 36 && height > 24;
            return (
              <g>
                <rect x={x + 1} y={y + 1} width={Math.max(width - 2, 0)} height={Math.max(height - 2, 0)} fill={color} rx={5} ry={5} />
                {showLabel && (
                  <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="0.65rem" fontWeight={700}>
                    {name}
                  </text>
                )}
              </g>
            );
          }}
        />
      </ResponsiveContainer>
    </Card>
  );
}

function InquiryCard({ reportData }) {
  return (
    <Card title="Inquiry vs. Advocacy" caption="Questions vs. comments per person">
      <CommentsVsQuestionsChart inquiry={reportData.inquiry} printMode />
    </Card>
  );
}

function BrevityCard({ reportData }) {
  return (
    <Card title="Brevity" caption="Avg. talk time per turn — target 20–30s">
      <AverageTalkTimeChart participation={reportData.participation} colors={COLORS} printMode />
    </Card>
  );
}

function FrequencyCard({ reportData }) {
  return (
    <Card title="Speaking Frequency" caption="Center = most frequent, circle = balanced">
      <FrequencyChart frequency={reportData.frequency} printMode />
    </Card>
  );
}

function DashboardPage({ caption, gridTemplateRows, children }) {
  return (
    <PrintLayout>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '4% 5%', background: '#f5f4f0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#1e2e45', marginBottom: 2 }}>
          Team Dynamics
        </div>
        <div style={{ fontSize: 10, color: '#6b757e', marginBottom: 14 }}>{caption}</div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows, gap: 14, minHeight: 0 }}>
          {children}
        </div>
      </div>
    </PrintLayout>
  );
}

export default function PrintDashboard4Up({ reportData }) {
  const speakers = reportData.participation?.speakers || [];
  const treemapData = speakers
    .slice()
    .sort((a, b) => (b.pct || 0) - (a.pct || 0))
    .map((p, i) => ({
      name: p.name.split('@')[0],
      size: p.pct || 1,
      color: COLORS[i % COLORS.length],
    }));

  if (speakers.length > SPLIT_THRESHOLD) {
    return (
      <>
        <DashboardPage caption="Air time and inquiry balance across the team." gridTemplateRows="1fr">
          <TreemapCard treemapData={treemapData} />
          <InquiryCard reportData={reportData} />
        </DashboardPage>
        <DashboardPage caption="Brevity and speaking frequency across the team." gridTemplateRows="1fr">
          <BrevityCard reportData={reportData} />
          <FrequencyCard reportData={reportData} />
        </DashboardPage>
      </>
    );
  }

  return (
    <DashboardPage caption="Air time, inquiry, brevity, and speaking frequency across the team." gridTemplateRows="1fr 1fr">
      <TreemapCard treemapData={treemapData} />
      <InquiryCard reportData={reportData} />
      <BrevityCard reportData={reportData} />
      <FrequencyCard reportData={reportData} />
    </DashboardPage>
  );
}
